import { useAppSelector } from "@/store/store-hooks";
import { selectTags } from "@/store/tags/tags-selectors";
import { Tag, UNTAGGED } from "@/store/tags/tags-slice";
import { FilterMenuOptionType, NoTranslate } from "@faro-lotv/flat-ui";
import { useMemo } from "react";
import {
  UseElementTagOptionsReturn,
  useElementTagOptions,
} from "./use-element-tag-options";

type UseProjectTagOptionsProps = {
  /** Whether dark styles should be used. */
  dark?: boolean;

  /** Whether the options should be disabled for interactions */
  disabled?: boolean;

  /** Callback when a tag is removed from the project. */
  onTagRemoved?(tag: Tag): void;
};

/** @returns the tag options for the user-assignable tags in a project */
export function useProjectTagOptions({
  dark,
  disabled,
  onTagRemoved,
}: UseProjectTagOptionsProps): UseElementTagOptionsReturn {
  const projectTags = useAppSelector(selectTags);
  const tags = useMemo(
    () => projectTags.filter(isProjectTagAssignable),
    [projectTags],
  );

  return useElementTagOptions({
    dark,
    disabled,
    tags,
    onTagRemoved,
  });
}

/**
 * @returns a list of uneditable tag options
 * @param projectTags All tags in the project
 */
export function createStaticProjectTagOptions(
  projectTags: Tag[],
): FilterMenuOptionType[] {
  return projectTags
    .filter(isProjectTagAssignable)
    .map((tag) => ({
      key: tag.id,
      value: tag.name,
      label: <NoTranslate>{tag.name}</NoTranslate>,
    }))
    .sort((a, b) => a.value.localeCompare(b.value));
}

function isProjectTagAssignable(tag: Tag): boolean {
  // The untagged tag is not user-assignable, but only available to filter by in the project.
  return tag.name !== UNTAGGED.name;
}
